import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import MaintenanceCalendar from './components/MaintenanceCalendar';
import TechnicianAssignment from './components/TechnicianAssignment';
import FacilityImpactPanel from './components/FacilityImpactPanel';
import PreventiveMaintenanceAlerts from './components/PreventiveMaintenanceAlerts';
import { equipmentLifecycleService } from '../../lib/equipmentLifecycleService';

const AutomatedMaintenanceScheduling = () => {
  const { userProfile } = useAuth();
  const [equipment, setEquipment] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('calendar');

  const hasAccess = ['owner', 'manager', 'sysadmin', 'staff']?.includes(userProfile?.role);
  const gymId = userProfile?.gym_id;

  useEffect(() => {
    if (hasAccess && gymId) {
      loadData();
    }
  }, [hasAccess, gymId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [equipmentRes, workOrdersRes] = await Promise.all([
        equipmentLifecycleService?.getEquipment(gymId),
        equipmentLifecycleService?.getWorkOrders(gymId)
      ]);

      if (equipmentRes?.data) setEquipment(equipmentRes?.data);
      if (workOrdersRes?.data) setWorkOrders(workOrdersRes?.data);

      // Mock technicians data (in production, fetch from user_profiles with staff role)
      setTechnicians([
        { id: '1', name: 'John Smith', skills: ['HVAC', 'Electrical'], availability: 'available', currentWorkload: 2 },
        { id: '2', name: 'Sarah Johnson', skills: ['Plumbing', 'General'], availability: 'busy', currentWorkload: 5 },
        { id: '3', name: 'Mike Davis', skills: ['Equipment', 'Electrical'], availability: 'available', currentWorkload: 1 },
        { id: '4', name: 'Emily Chen', skills: ['HVAC', 'Equipment'], availability: 'available', currentWorkload: 3 }
      ]);
    } catch (error) {
      console.error('Error loading maintenance scheduling data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedHeader />
        <div className="page-content-wrapper">
          <DashboardNavigation />
        </div>
        <div className="container mx-auto px-4 py-8">
          <div className="bg-card rounded-xl p-8 border border-border text-center">
            <Icon name="ShieldAlert" size={48} className="mx-auto mb-4 text-error" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
            <p className="text-muted-foreground">You do not have permission to access maintenance scheduling.</p>
          </div>
        </div>
      </div>
    );
  }

  const views = [
    { id: 'calendar', label: 'Calendar', icon: 'Calendar' },
    { id: 'technicians', label: 'Technicians', icon: 'Users' },
    { id: 'impact', label: 'Facility Impact', icon: 'AlertTriangle' },
    { id: 'alerts', label: 'Alerts', icon: 'Bell' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <DashboardNavigation />

      <div className="container mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Automated Maintenance Scheduling</h1>
            <p className="text-sm md:text-base text-muted-foreground">Intelligent preventive maintenance with technician assignment</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={loadData}
              size="sm"
            >
              <span className="hidden md:inline">Refresh</span>
            </Button>
            <Button
              variant="primary"
              iconName="Plus"
              iconPosition="left"
              size="sm"
            >
              Schedule Maintenance
            </Button>
          </div>
        </div>

        {/* View Tabs - Mobile Optimized */}
        <div className="bg-card rounded-xl border border-border mb-6">
          <div className="flex items-center gap-1 p-2 overflow-x-auto custom-scrollbar">
            {views?.map((view) => (
              <button
                key={view?.id}
                onClick={() => setActiveView(view?.id)}
                className={`flex items-center gap-2 px-3 md:px-4 py-2.5 rounded-lg whitespace-nowrap transition-all text-sm md:text-base ${
                  activeView === view?.id
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={view?.icon} size={18} />
                <span className="font-medium">{view?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading maintenance schedule...</p>
            </div>
          </div>
        ) : (
          <>
            {activeView === 'calendar' && (
              <MaintenanceCalendar
                equipment={equipment}
                workOrders={workOrders}
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                onRefresh={loadData}
              />
            )}
            {activeView === 'technicians' && (
              <TechnicianAssignment
                technicians={technicians}
                workOrders={workOrders}
                equipment={equipment}
                onRefresh={loadData}
              />
            )}
            {activeView === 'impact' && (
              <FacilityImpactPanel
                equipment={equipment}
                workOrders={workOrders}
                onRefresh={loadData}
              />
            )}
            {activeView === 'alerts' && (
              <PreventiveMaintenanceAlerts
                equipment={equipment}
                workOrders={workOrders}
                onRefresh={loadData}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AutomatedMaintenanceScheduling;